import React, { useState, useRef, useEffect } from 'react';
import { Phone, TrendingUp, CheckCircle } from 'lucide-react';

// "Talk to Alfred now" — pick a call → enter name + phone → consent → Alfred
// calls you back in ~a minute. The actual outbound call is placed by
// AlfredVoice_V2 (the 346 number); this form POSTs the request to a
// configurable endpoint that V2 owns.
//
// V2 ENDPOINT CONTRACT — implement a handler that accepts POST JSON:
//   { role: 'receptionist'|'sales'|'support',                  // legacy, still sent
//     persona: 'driver-screening'|'front-desk', // what to actually run
//     firstName: string, phone: string, callConsent: true, textConsent: boolean,
//     consentAt: ISO8601, source: 'marketing-site' }
//   → place an outbound call from +1 (346) 258-0006 to `phone`, running the
//     requested `persona`. Return 2xx on success; { error: 'code' } + 4xx/5xx on failure.
//
// `role` is the pre-existing wire value and is kept so an unchanged V2 handler
// still places a call. `persona` is the outbound-specific script. Until V2
// honors `persona`, the driver-screening card falls back to the generic `sales`
// script — the label promises more than that, so V2 needs the driver-screening
// persona before this is fully honest.
// Set the URL via VITE_DEMO_CALL_ENDPOINT (defaults to /api/demo-call).

const DEMO_NUMBER = '(346) 258-0006';
const ENDPOINT = import.meta.env.VITE_DEMO_CALL_ENDPOINT || '/api/demo-call';
const PHONE_RE = /^[+]?[\d\s().-]{10,20}$/;

// Editable — the calls a visitor can ask Alfred to place.
export const ROLES = [
  {
    id: 'driver-screening',
    role: 'sales',
    name: 'Driver screening call',
    tagline: 'Recruiting',
    blurb: 'Alfred calls you as a driver applicant, screens against the criteria, and sets the handoff.',
    Icon: TrendingUp
  },
  {
    id: 'front-desk',
    role: 'receptionist',
    name: 'Front desk',
    tagline: 'Inbound',
    blurb: 'Alfred answers, books appointments, takes messages, and routes calls — 24/7.',
    Icon: Phone
  }
];

/**
 * @param {string[]} [roleIds]   Which calls to offer, in order. Defaults to all of them.
 * @param {string}   [defaultRole]  Preselect this call.
 * @param {boolean}  [showHeading]  Render the widget's own heading. Off when the
 *                                  containing section already supplies one.
 */
export default function TalkToAlfred({ roleIds, defaultRole = null, showHeading = true }) {
  const roles = roleIds?.length ? roleIds.map((id) => ROLES.find((r) => r.id === id)).filter(Boolean) : ROLES;
  const [selected, setSelected] = useState(defaultRole);
  const [firstName, setFirstName] = useState('');
  const [phone, setPhone] = useState('');
  const [callConsent, setCallConsent] = useState(false);
  const [textConsent, setTextConsent] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | submitting | done | error
  const [errorMsg, setErrorMsg] = useState('');

  const phoneValid = PHONE_RE.test(phone.trim());
  const canSubmit = selected && firstName.trim().length > 0 && phoneValid && callConsent && status !== 'submitting';

  // When a role is picked, scroll the form (name / number / consent) into view
  // so the next step is obvious. A preselected default must not yank the page.
  const formRef = useRef(null);
  const userPicked = useRef(false);
  useEffect(() => {
    if (selected && userPicked.current && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [selected]);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus('submitting'); setErrorMsg('');
    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role: ROLES.find((r) => r.id === selected)?.role || 'sales',
          persona: selected,
          firstName: firstName.trim(),
          phone: phone.trim(),
          callConsent: true,
          textConsent,
          consentAt: new Date().toISOString(),
          source: 'marketing-site'
        })
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Request failed (${res.status})`);
      }
      setStatus('done');
    } catch (err) {
      setErrorMsg('We couldn\'t place your call just now. Please try again in a moment.');
      setStatus('error');
    }
  };

  if (status === 'done') {
    const role = ROLES.find((r) => r.id === selected);
    return (
      <div className="max-w-xl mx-auto text-center bg-warm border border-hair rounded-brand p-10">
        <CheckCircle className="w-12 h-12 text-brass mx-auto mb-4" />
        <h3 className="text-2xl font-bold mb-2">On it — keep your phone handy.</h3>
        <p className="text-slateink font-light">
          Alfred will place a <strong>{role?.name.toLowerCase()}</strong> to <strong>{phone.trim()}</strong> in
          about a minute, from <strong>{DEMO_NUMBER}</strong>.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      {showHeading && (
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-3">Hear Alfred for yourself</h2>
          <p className="text-lg text-slateink font-light">
            Pick the call you want to hear, drop your number, and Alfred calls you in about a minute — from {DEMO_NUMBER}.
          </p>
        </div>
      )}

      {/* Call cards */}
      <div className={`grid grid-cols-1 gap-5 ${
        roles.length > 2 ? 'sm:grid-cols-3' : roles.length === 2 ? 'sm:grid-cols-2 max-w-3xl mx-auto' : 'max-w-md mx-auto'
      }`}>
        {roles.map(({ id, name, tagline, blurb, Icon }) => {
          const isSel = selected === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => { userPicked.current = true; setSelected(id); setStatus('idle'); }}
              aria-pressed={isSel}
              className={`text-left rounded-brand border p-6 transition-colors bg-paper ${
                isSel
                  ? 'border-brass ring-1 ring-brass'
                  : 'border-hair hover:border-brass'
              }`}
            >
              <div className="w-12 h-12 rounded-xl bg-ink text-white flex items-center justify-center mb-4">
                <Icon className="w-6 h-6" />
              </div>
              <span className="av-eyebrow">{tagline}</span>
              <h3 className="text-xl font-bold mt-1">{name}</h3>
              <p className="text-sm text-slateink font-light mt-2 leading-relaxed">{blurb}</p>
              <span className={`inline-block mt-4 text-xs font-semibold uppercase tracking-[0.14em] ${isSel ? 'text-brass' : 'text-slateink'}`}>
                {isSel ? '✓ Selected' : 'Pick this →'}
              </span>
            </button>
          );
        })}
      </div>

      {/* Form — appears once a role is picked */}
      {selected && (
        <form ref={formRef} onSubmit={submit} className="mt-10 max-w-xl mx-auto bg-warm border border-hair rounded-brand p-6 sm:p-8 scroll-mt-24">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-ink mb-1" htmlFor="ta-name">First name</label>
              <input
                id="ta-name" type="text" autoComplete="given-name"
                value={firstName} onChange={(e) => setFirstName(e.target.value)}
                placeholder="What should Alfred call you?"
                className="w-full px-4 py-3 rounded-xl bg-paper border border-hair focus:border-brass focus:ring-1 focus:ring-brass outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-ink mb-1" htmlFor="ta-phone">Phone number</label>
              <input
                id="ta-phone" type="tel" inputMode="tel" autoComplete="tel"
                value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 555-5555"
                className="w-full px-4 py-3 rounded-xl bg-paper border border-hair focus:border-brass focus:ring-1 focus:ring-brass outline-none"
              />
              {phone && !phoneValid && <p className="mt-1 text-sm text-red-600">Enter a valid phone number.</p>}
            </div>
          </div>

          <label className="flex items-start gap-3 text-sm text-slateink font-light mt-5">
            <input type="checkbox" checked={callConsent} onChange={(e) => setCallConsent(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-hair text-brass focus:ring-brass" />
            <span>
              By checking this box, I give my express consent for <strong>Alfred Voice</strong> to place an{' '}
              <strong>automated, AI-powered phone call</strong> to the number I provided for a product demonstration.
              Consent is not a condition of any purchase, and message &amp; data rates may apply. See our{' '}
              <a href="/privacy" target="_blank" rel="noreferrer" className="text-brass underline hover:text-brassdeep">Privacy Policy</a>
              {' '}and{' '}
              <a href="/terms" target="_blank" rel="noreferrer" className="text-brass underline hover:text-brassdeep">Terms</a>.
            </span>
          </label>

          <label className="flex items-start gap-3 text-sm text-slateink font-light mt-3">
            <input type="checkbox" checked={textConsent} onChange={(e) => setTextConsent(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-hair text-brass focus:ring-brass" />
            <span>Also text me about my demo (optional). Msg &amp; data rates may apply. Reply STOP to opt out.</span>
          </label>

          {status === 'error' && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{errorMsg}</div>
          )}

          <button
            type="submit" disabled={!canSubmit}
            className="av-btn-brass mt-6 w-full sm:w-auto px-8 py-4 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Phone className="w-5 h-5" />
            {status === 'submitting' ? 'Calling you…' : 'Call me now'}
          </button>
          {!callConsent && <p className="mt-3 text-xs text-slateink">Check the call-consent box so Alfred can call you.</p>}
        </form>
      )}
    </div>
  );
}
